import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormCheckbox } from '@/components/form/FormCheckbox'
import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { FormSelect } from '@/components/form/FormSelect'
import { Button } from '@/components/ui/Button'
import type { StudentProfile } from '@/features/student/api/profile.api'
import { useUpdateProfile } from '@/features/student/hooks/useProfile'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'
import { humanizeEnum } from '@/utils/format'

const VISIBILITY_OPTIONS = ['public', 'recruiters_only', 'hidden'].map((value) => ({
  value,
  label: humanizeEnum(value),
}))

const schema = z.object({
  is_fresher: z.boolean(),
  total_experience_months: z.string().optional(),
  relevant_experience_months: z.string().optional(),
  notice_period_days: z.string().optional(),
  current_ctc: z.string().optional(),
  expected_ctc: z.string().optional(),
  salary_currency: z.string().length(3).optional().or(z.literal('')),
  profile_visibility: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

function toFormValues(profile: StudentProfile): FormValues {
  return {
    is_fresher: profile.isFresher,
    total_experience_months:
      profile.totalExperienceMonths != null ? String(profile.totalExperienceMonths) : '',
    relevant_experience_months:
      profile.relevantExperienceMonths != null ? String(profile.relevantExperienceMonths) : '',
    notice_period_days: profile.noticePeriodDays != null ? String(profile.noticePeriodDays) : '',
    current_ctc: profile.currentCtc != null ? String(profile.currentCtc) : '',
    expected_ctc: profile.expectedCtc != null ? String(profile.expectedCtc) : '',
    salary_currency: profile.salaryCurrency ?? 'INR',
    profile_visibility: profile.profileVisibility,
  }
}

export function PreferencesForm({
  profile,
  onSaved,
}: {
  profile: StudentProfile
  onSaved?: () => void
}) {
  const update = useUpdateProfile()
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toFormValues(profile) })

  const isFresher = watch('is_fresher')

  const onSubmit = handleSubmit((values) => {
    const num = (v: string | undefined) => (v ? Number(v) : undefined)
    const payload = {
      is_fresher: values.is_fresher,
      total_experience_months: isFresher ? 0 : num(values.total_experience_months),
      relevant_experience_months: isFresher ? 0 : num(values.relevant_experience_months),
      notice_period_days: num(values.notice_period_days),
      current_ctc: num(values.current_ctc),
      expected_ctc: num(values.expected_ctc),
      salary_currency: values.salary_currency || undefined,
      profile_visibility: values.profile_visibility || undefined,
    }
    update.mutate(payload, { onSuccess: onSaved })
  })

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      <FormErrorSummary
        errors={errors}
        serverErrors={getFieldErrors(update.error)}
        generalMessage={update.error ? getErrorMessage(update.error) : null}
      />
      <FormCheckbox
        control={control}
        name="is_fresher"
        label="I'm a fresher (no prior work experience)"
      />
      {!isFresher && (
        <div className="row">
          <div className="col-md-6">
            <FormInput
              control={control}
              name="total_experience_months"
              label="Total Experience (months)"
              type="number"
            />
          </div>
          <div className="col-md-6">
            <FormInput
              control={control}
              name="relevant_experience_months"
              label="Relevant Experience (months)"
              type="number"
            />
          </div>
        </div>
      )}
      <FormInput
        control={control}
        name="notice_period_days"
        label="Notice Period (days)"
        type="number"
      />
      <div className="row">
        <div className="col-md-4">
          <FormInput control={control} name="current_ctc" label="Current CTC" type="number" />
        </div>
        <div className="col-md-4">
          <FormInput control={control} name="expected_ctc" label="Expected CTC" type="number" />
        </div>
        <div className="col-md-4">
          <FormInput control={control} name="salary_currency" label="Currency (e.g. INR)" />
        </div>
      </div>
      <FormSelect
        control={control}
        name="profile_visibility"
        label="Profile Visibility"
        options={VISIBILITY_OPTIONS}
      />
      <Button type="submit" loading={update.isPending}>
        Save
      </Button>
    </form>
  )
}
